<template>
  <v-main>
    <AlertsComponent context="payment" />
    <div class="px-8 pt-8">
      <!-- Please review your payment information before proceeding -->
      <v-row wrap class="mb-4">
        <v-col>
          <h4>{{ $t('L_please_review_your_payment_information_before') }}</h4>
        </v-col>
      </v-row>

      <!-- Payment Amount -->
      <v-row class="lightBlue py-2 pl-0 pr-4 mb-4" :class="$vuetify.display.xs ? 'ml-0' : 'ml-0'">
        <v-col :cols="hasMultipleOptions ? 6 : 12" class="mb-2">
          <span class="font-weight-regular">{{ $t('L_payment_amount') }}:</span>
        </v-col>
        <v-col
          v-if="hasMultipleOptions"
          xs="6"
          class="mt-2 pr-4 text-right"
          :class="$vuetify.display.xs ? 'mt-0' : ''"
        >
          <OnexButton @click="goBackToPaymentAmount" :disabled="submitClicked">
            {{ $t('L_change') }}
          </OnexButton>
        </v-col>
        <v-col cols="12" class="font-weight-bold mb-2">
          {{ getPaymentAmount }}
        </v-col>
      </v-row>

      <!-- Payment Method -->
      <v-row class="lightBlue py-2 pl-0 pr-4 mt-4" :class="$vuetify.display.xs ? 'ml-0' : 'ml-0'">
        <v-col cols="6" class="tmb-4">
          <span class="text-left font-weight-regular">{{ $t('L_payment_method') }}:</span>
        </v-col>
        <v-col xs="6" class="mt-2 pr-4 text-right" :class="$vuetify.display.xs ? 'mt-0' : ''">
          <OnexButton @click="goBackToPaymentMethod" :disabled="submitClicked">
            {{ $t('L_change') }}
          </OnexButton>
        </v-col>
        <v-col cols="12" class="mb-4">
          <span class="font-size--16 font-weight-bold">{{ getPaymentMethod }}</span>
        </v-col>
        <v-col cols="12" class="mb-2">
          <span class="font-size--14 font-weight-regular">{{ getBankName }}</span>
        </v-col>
      </v-row>

      <!-- LoadingIndicator -->
      <v-card flat>
        <v-card-text>
          <v-col class="pa-3">
            <v-row>
              <v-col>
                <LoadingIndicator :loading="submitClicked" />
              </v-col>
            </v-row>
          </v-col>
        </v-card-text>
      </v-card>

      <!-- Disclaimer -->
      <v-card class="setMaxHeightCard mt-8 mb-8" :rounded="false">
        <v-card-text>
          <v-row mt-4>
            <v-col cols="12" font-weight-bold font-size--16 mb-4>
              <span v-html="getDisclaimerTitle" />
            </v-col>
            <v-col cols="12" font-weight-regular font-size--14 mb-8>
              <span v-html="getDisclaimerBody" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- By clicking, 'I Authorize' -->
      <v-row class="mt-4 pt-4">
        <v-col cols="12" class="mb-4">
          {{ $t('L_by_clicking_I_authorize_I_certify_that_I_have_read') }}
        </v-col>
      </v-row>

      <!-- Action Buttons -->
      <v-row class="d-flex justify-end my-4">
        <v-col cols="12" sm="4" md="3" class="px-4">
          <OnexButton
            block
            variant="outlined"
            color="outlined"
            :disabled="submitClicked"
            @click="cancel"
          >
            {{ $t('L_cancel') }}
          </OnexButton>
        </v-col>
        <v-col cols="12" sm="4" md="3" class="px-4">
          <OnexButton block :disabled="!checked || submitClicked" @click="callSubmitPayment">
            {{ $t('L_i_authorize') }}
          </OnexButton>
        </v-col>
      </v-row>

      <MessageDialog
        :show-dialog="dialog"
        type="error"
        :title="$t('L_submit_ach_error_title')"
        :message="$t('L_submit_ach_error_message')"
        :is-persistent="true"
        @buttonOneAction="dialog = false"
      />
    </div>
  </v-main>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import MessageDialog from '@/components/MessageDialog.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { mapActions, mapState, mapStores } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import AlertsComponent from '@/components/AlertsComponent.vue';
import { OnexButton } from '@primerica/onex-ui-lib';

export const formattedName = (name: string) => {
  const fullName = name.split(' ');
  const fname = fullName.slice(fullName.length - 1);
  const sname = fullName.slice(0, fullName.length - 1).join(' ');
  return `${fname}, ${sname}`;
};

const componentOptions: ComponentOptions = {
  name: 'PayStepThreeACH',
  components: {
    MessageDialog,
    LoadingIndicator,
    AlertsComponent,
    OnexButton,
  },
  props: {
    selectedAmount: {
      type: String,
      required: true,
      default: '0',
    },
  },
  data: () => ({
    dialog: false,
    checked: true,
    submitClicked: false,
    // text: undefined,
    // progressLoading: false,
    // documentContent: undefined,
  }),
  computed: {
    ...mapStores(usePolicyStore),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(usePolicyStore, [
      'policies',
      'currentPolicy',
      'routeNameToNavigateBack',
      'selectedPolicyIndex',
      'paymentData',
      'achSubmissionLoading',
    ]),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    getPaymentAmount() {
      if (this.paymentData?.amount) {
        return `${this.paymentData.amount}`;
      }
      return '';
    },
    getPaymentMethod() {
      if (this.paymentData?.accountNumber) {
        return `****${this.paymentData?.accountNumber?.slice(-4)} ${this.paymentData.accountType}`;
      }
      return '';
    },
    getBankName() {
      if (this.paymentData?.bankName) {
        return this.paymentData.bankName;
      }
      return '';
    },
    isCanadaPolicy() {
      return !!this.currentPolicy?.isCanadaPolicy;
    },
    hasMultipleOptions() {
      return this.currentPolicy?.paynowInfo?.hasMultipleOptions;
    },
    getDisclaimerTitle() {
      return this.isCanadaPolicy
        ? this.$t('L_disclaimer_title_ca')
        : this.$t('L_disclaimer_title_common');
      // return this.$t('L_disclaimer_title_common');
    },
    getDisclaimerBody() {
      return this.isCanadaPolicy
        ? this.$t('L_payment_option_disclaimer_body_ca')
        : this.$t('L_payment_option_disclaimer_body_us');
    },
    routingNumber() {
      return this?.paymentData?.routingNumber || '';
    },
    accountNumber() {
      return this?.paymentData?.accountNumber || '';
    },
  },
  methods: {
    ...mapActions(usePolicyStore, ['submitAch']),
    ...mapActions(useGlobalStore, {
      removeAlerts: 'removeAlerts',
      addError: 'addError',
    }),
    goBackToPaymentAmount() {
      // * Dynatrace Tag (https://primerica.atlassian.net/wiki/spaces/PD/pages/19955873/Payments+-+Custom+Dynatrace+Tags)
      try {
        const i = this.$dynatrace.enterAction('OneTimePayment_ChangeAmount_Selected');
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
      this.$emit('goBackToPaymentAmount');
    },
    goBackToPaymentMethod() {
      // * Dynatrace Tag (https://primerica.atlassian.net/wiki/spaces/PD/pages/19955873/Payments+-+Custom+Dynatrace+Tags)
      try {
        const i = this.$dynatrace.enterAction('OneTimePayment_ChangePaymentMethod_Selected');
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
      this.$emit('goBackToPaymentMethod');
    },
    showSuccessBanner() {
      this.$emit('goToStepFour');
    },
    showErrorBanner(error: string) {
      this.addError({ error: { key: this.$t(error) }, context: 'payment' });
    },
    cancel() {
      // * Dynatrace Tag (https://primerica.atlassian.net/wiki/spaces/PD/pages/19955873/Payments+-+Custom+Dynatrace+Tags)
      try {
        const i = this.$dynatrace.enterAction('PC2_PolicyReview_Cancel');
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
      this.$emit('cancelDialog');
    },
    async callSubmitPayment() {
      this.removeAlerts('payment');
      try {
        this.submitClicked = true;
        this.achSubmissionLoading = true;
        const name = formattedName(this.paymentData?.accountHolder);
        const paymentInfo = {
          draftAmount: this?.selectedAmount.replaceAll(',', ''),
          emailAddress: this.customerProfile.email,
          language: this.locale,
          bankAccount: {
            accountName: name,
            routingNumber: this.routingNumber,
            accountNumber: this.accountNumber,
            accountType: this?.paymentData?.accountCode === '1' ? '3' : ' ',
          },
        };
        const response = await this.submitAch({
          paymentInfo,
          policyNumber: this?.currentPolicy?.contractId,
        }).catch((err: unknown) => {
          // * Dynatrace Tag (https://primerica.atlassian.net/wiki/spaces/PD/pages/19955873/Payments+-+Custom+Dynatrace+Tags)
          try {
            const i = this.$dynatrace.enterAction(
              `OneTimePayment_ACH_PaymentSubmission_Error_${err}`
            );
            this.$dynatrace.leaveAction(i);
          } catch (_error) {
            /* empty */
          }
        });
        if (response) {
          try {
            await this.policyStore.getPolicyOverview();
            const i = this.$dynatrace.enterAction('OneTimePayment_ACH_PaymentSubmission_Succeeded');
            this.$dynatrace.leaveAction(i);
            const j = this.$dynatrace.enterAction('pay_now_succesful_submission');
            this.$dynatrace.leaveAction(j);
          } catch (_error) {
            /* empty */
          }
          this.$emit('goToStepFour');
        } else {
          try {
            const i = this.$dynatrace.enterAction('OneTimePayment_ACH_PaymentSubmission_Error');
            this.$dynatrace.leaveAction(i);
          } catch (_error) {
            /* empty */
          }
          this.showErrorBanner('L_error_occurred_while_processing_ach_payment');
        }
      } finally {
        this.achSubmissionLoading = false;
        this.policyStore.paymentInfoShowPaymentAmountDateBillingMethod = true;
        this.submitClicked = false;
      }
    },
  },
  mounted() {},
};

export default defineComponent(componentOptions);
</script>

<style>
.bg-light-grey {
  background-color: #f5f5f5;
}
.lightBlue {
  background-color: #f5f7fa;
}
.whiteLabel {
  color: #ffffff;
}
.setMaxHeightCard {
  display: flex !important;
  flex-direction: column;
  height: 200px;
  flex: 0 1 auto;
  overflow-y: auto;
}
.v-card__text {
  flex-grow: 1;
  overflow: auto;
}
</style>
